/**
 * Agent Registry v2 — API bindings for the per-org fleet inventory.
 *
 * 4 org-scoped endpoints + 2 platform-release endpoints.
 * Sort uses separate `sort` + `order` params (not `field:direction`).
 * Detail is agent_id only — no hostname fallback.
 */
import { apiGet, apiPut } from "@shared/api";
import {
  registryListSchema, type RegistryList,
  registrySummarySchema, type RegistrySummary,
  registryFiltersSchema, type RegistryFilters,
  registryAgentDetailSchema, type RegistryAgentDetail,
  agentReleaseSchema, type AgentRelease,
  pickerListSchema, type PickerList,
  type RegistryQuery,
} from "./registry.schemas";

const base = (orgId: string) =>
  `/organizations/${encodeURIComponent(orgId)}/agents`;

/** API 1 — filtered, sorted, paginated table + meta facets. */
export const listRegistryAgents = (
  orgId: string,
  query: RegistryQuery = {},
): Promise<RegistryList> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    per_page: query.per_page ?? 10,
  };
  if (query.search) params.search = query.search;
  if (query.status && query.status !== "all") params.status = query.status;
  if (query.os_family?.length) params.os_family = query.os_family.join(",");
  if (query.version?.length) params.version = query.version.join(",");
  if (query.sort) params.sort = query.sort;
  if (query.order) params.order = query.order;

  return apiGet(registryListSchema, base(orgId), params);
};

/** API 2 — fleet KPIs + OS donut + version donut. */
export const getRegistrySummary = (orgId: string): Promise<RegistrySummary> =>
  apiGet(registrySummarySchema, `${base(orgId)}/summary`);

/** API 3 — filter facets (call on page mount alongside the list). */
export const getRegistryFilters = (orgId: string): Promise<RegistryFilters> =>
  apiGet(registryFiltersSchema, `${base(orgId)}/filters`);

/** API — Picker: lean 4-field list for the TopNav agent selector.
 *  Sorted by last_seen desc so most-active agents surface first. */
export const listPickerAgents = (
  orgId: string,
  opts: { search?: string; limit?: number } = {},
): Promise<PickerList> => {
  const params: Record<string, string | number> = {};
  if (opts.search) params.search = opts.search;
  if (opts.limit) params.limit = opts.limit;
  return apiGet(pickerListSchema, `${base(orgId)}/picker`, params);
};

/** API 4 — single agent detail. agent_id only, no hostname fallback. */
export const getRegistryAgent = (
  orgId: string,
  agentId: string,
): Promise<RegistryAgentDetail> =>
  apiGet(registryAgentDetailSchema, `${base(orgId)}/${encodeURIComponent(agentId)}`);

/** API 5a — read latest agent version (any user). */
export const getAgentRelease = (): Promise<AgentRelease> =>
  apiGet(agentReleaseSchema, "/platform/agent-release");

/** API 5b — update latest version (Platform Admin only). */
export const updateAgentRelease = (body: {
  latest_version: string;
  minimum_version?: string;
  release_channel?: string;
  release_notes_url?: string;
}): Promise<AgentRelease> =>
  apiPut(agentReleaseSchema, "/platform/agent-release", body);
